import { graphql } from 'gatsby';
import {
  StyledButton,
  StyledDropdown,
  StyledWrapper,
  StyledDropdownItems,
  StyledSubheadline,
  StyledIcon,
} from './NavigationDropdown.styles';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import NavigationDropdownLink from './components/NavigationDropdownLink/NavigationDropdownLink';
import { useDropdownPositioning } from './hooks/useDropdownPositioning';
import { useDropdownKeyboardNavigation } from './hooks/useDropdownKeyboardNavigation';
import LoginLink from '../LoginSection/components/LoginLink/LoginLink';

// Touch gesture thresholds
const SCROLL_THRESHOLD_PX = 5;
const TAP_MOVEMENT_THRESHOLD_PX = 10;
const TAP_DURATION_THRESHOLD_MS = 300;

type NavigationDropdownProps = {
  navigationDropdownElements: Queries.NAVIGATION_DROPDOWNFragment['navigationDropdownElements'];
  buttonWidthFull?: boolean;
};
const NavigationDropdown = ({
  navigationDropdownElements: elements,
  buttonWidthFull,
}: NavigationDropdownProps) => {
  const {
    button_label,
    button_icon,
    button_variant,
    button_size,
    dropdown_subheadline,
    dropdown_items,
    dropdown_width,
  } = elements;
  const buttonLabel = button_label.value;
  const buttonIcon = button_icon?.value;
  const buttonVariant = button_variant?.value || 'primary';
  const buttonSize = button_size?.value || 'large';
  const dropdownSubheadline = dropdown_subheadline?.value;
  // Need the useMemo, otherwise the linter will complain about the array being recreated on every render.
  const dropdownItems = useMemo(() => {
    return [...(dropdown_items?.value || [])];
  }, [dropdown_items]);
  const dropdownWidthSize = dropdown_width?.value || 'medium';
  const buttonId = `options-menu-${buttonLabel.replace(/\s+/g, '-').toLowerCase()}`;

  const [isOpen, setIsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const { calculateDropdownPosition } = useDropdownPositioning(buttonRef, dropdownRef);

  const { handleItemKeyDown } = useDropdownKeyboardNavigation({
    isOpen,
    buttonRef,
    dropdownRef,
    itemRefs,
    toggleDropdown,
    setIsOpen,
  });

  // Track touch start position to detect scrolling intent
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasTouchMovedRef = useRef(false);

  // Track if component is mounted for portal rendering
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Custom outside click handler that's scroll-aware
  useEffect(() => {
    if (!isOpen) return;

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      touchStartPosRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      hasTouchMovedRef.current = false;
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!touchStartPosRef.current) return;

      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);

      // If moved more than threshold, it's a scroll
      if (deltaX > SCROLL_THRESHOLD_PX || deltaY > SCROLL_THRESHOLD_PX) {
        hasTouchMovedRef.current = true;
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const target = event.target as Node;

      // Ignore if the touch is inside the button wrapper or the dropdown itself
      if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }

      // If user has moved their finger, it's a scroll, not a tap
      if (hasTouchMovedRef.current) {
        return;
      }

      // It's a legitimate tap outside, close the dropdown
      setIsOpen(false);
    };

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside the button wrapper or the dropdown itself
      if (!wrapperRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    let animationFrameId: number;
    let scrollThrottleId: number;

    const throttledScrollHandler = () => {
      if (scrollThrottleId) {
        cancelAnimationFrame(scrollThrottleId);
      }
      scrollThrottleId = requestAnimationFrame(() => calculateDropdownPosition());
    };

    if (isOpen) {
      // Always calculate position when dropdown is opened
      calculateDropdownPosition();

      // Reset dropdown scroll position to top when dropdown opens
      if (dropdownRef.current) {
        dropdownRef.current.scrollTop = 0;
      }

      // Schedule the transition-in after the initial render cycle
      animationFrameId = requestAnimationFrame(() => {
        setIsTransitioning(true);
      });

      window.addEventListener('resize', calculateDropdownPosition, { passive: true });
      window.addEventListener('scroll', throttledScrollHandler, { passive: true });
    } else {
      setIsTransitioning(false);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (scrollThrottleId) {
        cancelAnimationFrame(scrollThrottleId);
      }
      window.removeEventListener('resize', calculateDropdownPosition);
      window.removeEventListener('scroll', throttledScrollHandler);
    };
  }, [isOpen, calculateDropdownPosition]);

  const handleButtonTouch = (event: React.TouchEvent) => {
    event.preventDefault(); // Prevent mouse event simulation
    toggleDropdown();
  };

  // Track touch movement on items to differentiate between tap and scroll
  const touchStartRef = useRef<{ x: number; y: number; timestamp: number } | null>(null);

  const handleItemTouchStart = useCallback((event: React.TouchEvent) => {
    const touch = event.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      timestamp: Date.now(),
    };
  }, []);

  const handleItemTouchEnd = useCallback((event: React.TouchEvent, index: number) => {
    if (!touchStartRef.current) return;

    const touch = event.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.timestamp;

    // If the touch moved less than threshold pixels and lasted less than threshold ms, treat it as a tap
    const isTap =
      deltaX < TAP_MOVEMENT_THRESHOLD_PX &&
      deltaY < TAP_MOVEMENT_THRESHOLD_PX &&
      deltaTime < TAP_DURATION_THRESHOLD_MS;

    if (isTap) {
      event.preventDefault();
      itemRefs.current[index]?.click();
    }

    touchStartRef.current = null;
  }, []);

  const handleLoginLinkRef = useCallback((el: HTMLDivElement | null, index: number) => {
    if (el) {
      // Find the button or anchor element inside the LoginLink
      const interactiveElement = el.querySelector('button, a');
      if (interactiveElement) {
        itemRefs.current[index] = interactiveElement as HTMLAnchorElement;
      }
    }
  }, []);

  const dropdownContentInner = useMemo(
    () => (
      <>
        {dropdownSubheadline && <StyledSubheadline>{dropdownSubheadline}</StyledSubheadline>}
        <nav aria-label="Dropdown navigation">
          <StyledDropdownItems>
            {dropdownItems.map((item, index) =>
              item.__typename === 'kontent_item_navigation_dropdown_link' ? (
                <NavigationDropdownLink
                  key={item.system.id}
                  {...item}
                  ref={(el) => (itemRefs.current[index] = el)}
                  tabIndex={0}
                  onTouchStart={handleItemTouchStart}
                  onTouchEnd={(event: React.TouchEvent) => handleItemTouchEnd(event, index)}
                  onKeyDown={(event: React.KeyboardEvent) => handleItemKeyDown(event, index)}
                />
              ) : (
                <div
                  key={item.system.id}
                  ref={(el) => handleLoginLinkRef(el, index)}
                  role="none"
                  onTouchStart={handleItemTouchStart}
                  onTouchEnd={(event: React.TouchEvent) => handleItemTouchEnd(event, index)}
                  onKeyDown={(event: React.KeyboardEvent) => handleItemKeyDown(event, index)}
                >
                  <LoginLink {...item} />
                </div>
              )
            )}
          </StyledDropdownItems>
        </nav>
      </>
    ),
    [
      dropdownSubheadline,
      dropdownItems,
      handleItemTouchStart,
      handleItemTouchEnd,
      handleItemKeyDown,
      handleLoginLinkRef,
    ]
  );

  // Wrapper that includes dynamic styling state but doesn't cause content re-renders
  const dropdownContent = (
    <StyledDropdown
      ref={dropdownRef}
      width={dropdownWidthSize}
      aria-labelledby={buttonId}
      isOpen={isOpen}
      isTransitioning={isTransitioning}
      aria-hidden={!isOpen}
    >
      {dropdownContentInner}
    </StyledDropdown>
  );

  return (
    <StyledWrapper buttonWidthFull={buttonWidthFull} ref={wrapperRef}>
      <StyledButton
        ref={buttonRef}
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        id={buttonId}
        buttonWidthFull={buttonWidthFull}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
        onTouchEnd={handleButtonTouch}
      >
        {buttonLabel}
        {buttonIcon && <StyledIcon {...buttonIcon} data-testid="navigation-dropdown-icon" />}
      </StyledButton>
      {isMounted && createPortal(dropdownContent, document.body)}
    </StyledWrapper>
  );
};

export const fragment = graphql`
  fragment NAVIGATION_DROPDOWN on kontent_item_navigation_dropdown {
    __typename
    system {
      codename
    }
    navigationDropdownElements: elements {
      button_label {
        value
      }
      button_icon {
        value {
          url
          description
        }
      }
      button_variant {
        value
      }
      button_size {
        value
      }
      dropdown_subheadline {
        value
      }
      dropdown_items {
        value {
          __typename
          ... on kontent_item {
            system {
              id
            }
          }
          ...NAVIGATION_DROPDOWN_LINK
          ...LOGIN_LINK
        }
      }
      dropdown_width {
        value
      }
    }
  }
`;

export default memo(NavigationDropdown);
